import type { CardPreviewProps } from "@/components/shapes/card-previews";
import {
  PreviewBody,
  PreviewTitle,
} from "@/components/shapes/shared/card-preview-parts";
import { useT } from "@/i18n";
import { framesDatasetMessages } from "@/i18n/messages/frames-dataset";
import { cn } from "@/lib/cn";
import { frameDisplayTitle } from "@/lib/frame-display-title";
import { derivedFieldIds } from "./dataset-columns";
import { cellText, datasetSummaryFrom, isNumericField } from "./dataset-values";

const MAX_ROWS = 5;
const MAX_FIELDS = 3;

/**
 * A Data frame: its title, the muted row count and a mini table of the first
 * five rows by the first three fields, read from the server's summary. A key
 * made of other fields is left out, as the opened table hides it.
 */
export function DatasetCardPreview({
  frame,
  source,
  density,
}: CardPreviewProps) {
  const t = useT(framesDatasetMessages);
  const summary = datasetSummaryFrom(source);
  const rows = summary.rows.slice(0, MAX_ROWS);
  const derived = derivedFieldIds(summary.fields, rows);
  const fields = summary.fields
    .filter((field) => !derived.has(field.id))
    .slice(0, MAX_FIELDS);
  return (
    <PreviewBody density={density} cap="max-h-[12em]">
      <div data-testid="dataset-card" className="flex min-h-0 flex-col">
        <PreviewTitle>{frameDisplayTitle(frame)}</PreviewTitle>
        <p
          className="text-[0.84em] text-card-muted"
          data-testid="dataset-card-count"
        >
          {t("rowCount", { count: summary.rowCount })}
        </p>
        {rows.length === 0 ? (
          <p
            className="mt-[0.6em] text-[0.84em] text-card-muted"
            data-testid="dataset-card-empty"
          >
            {t("noRows")}
          </p>
        ) : (
          <table className="mt-[0.6em] w-full table-fixed border-collapse text-[0.84em]">
            <thead>
              <tr className="text-card-muted">
                {fields.map((field) => (
                  <th
                    key={field.id}
                    scope="col"
                    className={cn(
                      "truncate pb-[0.3em] pr-[0.6em] font-medium",
                      isNumericField(field.type) ? "text-right" : "text-left",
                    )}
                  >
                    {field.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr
                  key={row.id}
                  className="text-card-ink"
                  data-testid={`dataset-card-row-${index}`}
                >
                  {fields.map((field) => (
                    <td
                      key={field.id}
                      className={cn(
                        "truncate py-[0.15em] pr-[0.6em]",
                        isNumericField(field.type) && "text-right tabular-nums",
                      )}
                    >
                      {cellText(row.values[field.id], field.type)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </PreviewBody>
  );
}
